/**
 * Calculate scholarship status based on the provided SQL case logic
 * @param {Object} student - Student record object
 * @returns {string} - Scholarship status string
 */
export const calculateScholarshipStatus = (student) => {
  if (!student || typeof student !== "object") return "";

  const { CTE_requirement, HSGY, SCCID, Sem7GPA, Sem8GPA } = student;

  // Convert values to appropriate types for comparison with better error handling
  const cteRequirement = CTE_requirement === "True" || CTE_requirement === true;
  const hsgy = parseInt(HSGY) || 0;
  const sccId = (SCCID || "").toString().trim();
  const sem7GPA = parseFloat(Sem7GPA) || 0;
  const sem8GPA = parseFloat(Sem8GPA) || 0;

  // Apply the SQL case logic

  // WHEN CTE_requirement = 'False' AND HSGY <= 2022 AND SccID <> '' THEN 'CTE Requirement: No'
  if (!cteRequirement && hsgy <= 2022 && sccId !== "") {
    return "CTE Requirement: No";
  }

  // WHEN CTE_requirement = 'False' AND HSGY <= 2022 THEN 'CTE Requirement: No'
  if (!cteRequirement && hsgy <= 2022) {
    return "CTE Requirement: No";
  }

  // WHEN Sem7GPA < 2.25 AND CTE_requirement = 'True' AND HSGY <= 2022 AND SccID = '' THEN 'Completer: No Scholarship, SC ID Needed'
  if (sem7GPA < 2.25 && cteRequirement && hsgy <= 2022 && sccId === "") {
    return "Completer: No Scholarship, SC ID Needed";
  }

  // WHEN Sem7GPA >= 2.25 AND CTE_requirement = 'True' AND HSGY <= 2022 AND SccID <> '' THEN 'Completer: Scholarship'
  if (sem7GPA >= 2.25 && cteRequirement && hsgy <= 2022 && sccId !== "") {
    return "Completer: Scholarship";
  }

  // WHEN Sem7GPA >= 2.25 AND CTE_requirement = 'True' AND HSGY <= 2022 AND SccID = '' THEN 'Completer: Scholarship, SC ID Needed'
  if (sem7GPA >= 2.25 && cteRequirement && hsgy <= 2022 && sccId === "") {
    return "Completer: Scholarship, SC ID Needed";
  }

  // WHEN Sem8GPA >= 2.25 AND cte_requirement = 'True' AND HSGY <= 2022 AND SccID <> '' THEN 'Completer: Scholarship'
  if (sem8GPA >= 2.25 && cteRequirement && hsgy <= 2022 && sccId !== "") {
    return "Completer: Scholarship";
  }

  // WHEN Sem8GPA >= 2.25 AND cte_requirement = 'True' AND HSGY <= 2022 AND SccID = '' THEN 'Completer: Scholarship, SC ID Needed'
  if (sem8GPA >= 2.25 && cteRequirement && hsgy <= 2022 && sccId === "") {
    return "Completer: Scholarship, SC ID Needed";
  }

  // WHEN Sem7GPA < 2.25 AND CTE_requirement = 'True' AND HSGY <= 2022 AND SccID <> '' THEN 'Completer: No Scholarship'
  if (sem7GPA < 2.25 && cteRequirement && hsgy <= 2022 && sccId !== "") {
    return "Completer: No Scholarship";
  }

  // WHEN Sem8GPA < 2.25 AND cte_requirement = 'True' AND HSGY <= 2022 AND SccID <> '' THEN 'Completer: No Scholarship'
  if (sem8GPA < 2.25 && cteRequirement && hsgy <= 2022 && sccId !== "") {
    return "Completer: No Scholarship";
  }

  // Default case (END)
  return "";
};

/**
 * Calculate final GPA based on the provided SQL case logic
 * @param {Object} student - Student record object
 * @returns {number} - Final GPA value
 */
export const calculateFinalGPA = (student) => {
  if (!student || typeof student !== "object") return 0;

  const { Sem7GPA, Sem8GPA } = student;

  // Convert values to appropriate types for comparison with better error handling
  const sem7GPA = parseFloat(Sem7GPA) || 0;
  const sem8GPA = parseFloat(Sem8GPA) || 0;

  // Apply the SQL case logic:
  // WHEN [@field:Sem8GPA] > 0 THEN [@field:Sem8GPA]
  if (sem8GPA > 0) {
    return sem8GPA;
  }

  // WHEN [@field:Sem8GPA] IS NULL AND [@field:Sem7GPA] > 0 THEN [@field:Sem7GPA]
  if (
    (Sem8GPA === null ||
      Sem8GPA === undefined ||
      Sem8GPA === "" ||
      sem8GPA === 0) &&
    sem7GPA > 0
  ) {
    return sem7GPA;
  }

  // WHEN [@field:Sem8GPA] is NULL AND [@field:Sem7GPA] IS NULL THEN 0
  return 0;
};

/**
 * Calculate senior packet status based on the provided SQL case logic
 * @param {Object} student - Student record object
 * @returns {boolean} - True if student qualifies for TP Scholarship Packet, false otherwise
 */
export const calculateSeniorPacket = (student) => {
  if (!student || typeof student !== "object") return false;

  const { CTE_requirement, HSGY, SCCID, Sem7GPA, Sem8GPA } = student;

  // Convert values to appropriate types for comparison
  const cteRequirement =
    CTE_requirement === "True" ||
    CTE_requirement === true ||
    CTE_requirement === -1 ||
    CTE_requirement === 1;
  const hsgy = parseInt(HSGY) || 0;
  const sccId = (SCCID || "").toString().trim();
  const sem7GPA = parseFloat(Sem7GPA) || 0;
  const sem8GPA = parseFloat(Sem8GPA) || 0;

  // Return true only for "TP Scholarship Packet" cases
  // WHEN HSGY = 2022 AND CTE_requirement = 'True' AND SccID <> '' AND (Sem7GPA >= 2.25 OR Sem8GPA >= 2.25)
  if (
    hsgy === 2022 &&
    cteRequirement &&
    sccId !== "" &&
    (sem7GPA >= 2.25 || sem8GPA >= 2.25)
  ) {
    return true;
  }

  // All other cases return false
  return false;
};

/**
 * Get detailed senior packet status text based on the provided SQL case logic
 * @param {Object} student - Student record object
 * @returns {string} - Detailed senior packet status string
 */
export const getSeniorPacketStatusText = (student) => {
  if (!student || typeof student !== "object") return "";

  const { CTE_requirement, HSGY, SCCID, Sem7GPA, Sem8GPA } = student;

  // Convert values to appropriate types for comparison
  const cteRequirement =
    CTE_requirement === "True" ||
    CTE_requirement === true ||
    CTE_requirement === -1 ||
    CTE_requirement === 1;
  const hsgy = parseInt(HSGY) || 0;
  const sccId = (SCCID || "").toString().trim();
  const sem7GPA = parseFloat(Sem7GPA) || 0;
  const sem8GPA = parseFloat(Sem8GPA) || 0;

  // Apply the SQL case logic for detailed status

  // WHEN HSGY = 2022 AND CTE_requirement = 'True' AND SccID <> '' AND (Sem7GPA >= 2.25 OR Sem8GPA >= 2.25)
  // THEN 'TP Scholarship Packet'
  if (
    hsgy === 2022 &&
    cteRequirement &&
    sccId !== "" &&
    (sem7GPA >= 2.25 || sem8GPA >= 2.25)
  ) {
    return "TP Scholarship Packet";
  }

  // WHEN HSGY = 2022 AND CTE_requirement = 'True' AND SccID <> '' AND (Sem7GPA < 2.25 AND Sem8GPA < 2.25)
  // THEN 'TP Completer, No Scholarship'
  if (
    hsgy === 2022 &&
    cteRequirement &&
    sccId !== "" &&
    sem7GPA < 2.25 &&
    sem8GPA < 2.25
  ) {
    return "TP Completer, No Scholarship";
  }

  // WHEN HSGY = 2022 AND CTE_requirement = 'True' AND SccID = ''
  // THEN 'TP Completer, No Tartan'
  if (hsgy === 2022 && cteRequirement && sccId === "") {
    return "TP Completer, No Tartan";
  }

  // WHEN HSGY = 2022 AND CTE_requirement = 'FALSE'
  // THEN 'Non-Completer'
  if (hsgy === 2022 && !cteRequirement) {
    return "Non-Completer";
  }

  // Default case (END)
  return "Not Applicable";
};

/**
 * Calculate scholarship eligibility based on the provided SQL case logic
 * @param {Object} student - Student record object
 * @returns {boolean} - True if student is eligible for scholarship, false otherwise
 */
export const calculateScholarshipEligible = (student) => {
  const status = calculateScholarshipStatus(student);
  const result =
    status.includes("Scholarship") && !status.includes("No Scholarship");

  // Debug for Seth Aaron specifically
  if (student && student.FirstName === "Seth" && student.LastName === "Aaron") {
    console.log("🔍 SETH AARON DEBUG:", {
      name: `${student.FirstName} ${student.LastName}`,
      HSGY: student.HSGY,
      HSGY_type: typeof student.HSGY,
      CTE_requirement: student.CTE_requirement,
      CTE_type: typeof student.CTE_requirement,
      calculatedStatus: status,
      finalResult: result,
      context: new Error().stack.includes("renderCell")
        ? "DataGrid"
        : "EditForm",
    });
  }

  // Return true only if the status contains "Scholarship" but NOT "No Scholarship"
  return result;
};
